"use client"

import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import AccountDeletionInitiatedModal from "../../components/modals/AccDeletionInitiatedModal"
import SuspendAccountModal from "../../components/modals/SuspendAccModal"
import DeleteAccountModal from "../../components/modals/DeleteAccountModal"
import SuspensionSuccessModal from "../../components/modals/SuspensionSuccessModal"
import Navbar from "../../components/account/Navbar"
import { useAuth } from "../../hooks/useAuth"
import { authAPI } from "../../services/authAPI"
import "./accountmanage.css"

// Import SVG icons from the public folder
import WarningSvg from "/icons/account/warning.svg"
import DeleteSvg from "/icons/account/delete.svg"
import CheckmarkSvg from "/icons/account/checkmark.svg"

// Account Management Content Component (for use within AccountPreferences)
const AccountManagementContent = () => {
  const navigate = useNavigate()
  const { userType } = useParams()
  const { t, i18n } = useTranslation()
  const { user } = useAuth() // Get user data from auth context
  const isRTL = i18n.dir() === "rtl"
  const [isAccountSuspended, setIsAccountSuspended] = useState(false)

  // Modal states
  const [showSuspendModal, setShowSuspendModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeletionInitiatedModal, setShowDeletionInitiatedModal] = useState(false)
  const [showSuspensionSuccessModal, setShowSuspensionSuccessModal] = useState(false)

  // Helper to fallback to localStorage if user context is stale
  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem("user_data")
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  const handleSuspendAccount = () => {
    setShowSuspendModal(true)
  }

  const handleConfirmSuspension = async (password) => {
    try {
      const storedUser = getStoredUser()
      // Use explicit fallback to ensure we catch the flag
      const isSocial = user?.has_social_login || storedUser.has_social_login

      const suspendData = {
        suspension_type: "TEMPORARY",
        reason: "User requested suspension",
        suspension_duration: "30_days", // Default duration
        password: password,
        oauth_mode: !!isSocial,
      }

      // Make the API call to suspend the account
      await authAPI.suspendAccount(suspendData)

      // Update UI state
      setIsAccountSuspended(true)
      setShowSuspendModal(false)

      // Show success modal
      setShowSuspensionSuccessModal(true)

    } catch (error) {
      // Extract error message from API response error object if available
      const message = error.response?.data?.message || error.message || t("accountManagement.messages.suspendError")
      alert(message)
    }
  }

  const handleDeleteAccount = () => {
    setShowDeleteModal(true)
  }

  const handleConfirmDeletion = async (reasonData) => {
    try {
      const storedUser = getStoredUser()
      const isSocial = storedUser.has_social_login

      const deletionData = {
        ...reasonData,
        oauth_mode: !!isSocial
      }
      await authAPI.deleteAccount(deletionData)
      setShowDeleteModal(false)
      setShowDeletionInitiatedModal(true)
    } catch (error) {
      console.error("Deletion failed:", error)
      // You might want to show an error message to the user here
      const message = error.response?.data?.message || error.message || t("accountManagement.messages.deleteError") || "Failed to delete account"
      alert(message)
      // Re-open modal if needed
      setShowDeleteModal(true)
    }
  }

  const handleSuspendInsteadOfDelete = () => {
    setShowDeleteModal(false)
    setShowSuspendModal(true)
  }

  const handleSuspensionSuccessRedirect = () => {
    // Redirect to dashboard or login page after account suspension
    navigate(`/dashboard/${userType}`)
  }

  return (
    <div className={`accountmanagement-container ${isRTL ? "rtl" : ""}`} dir={i18n.dir()}>
      <main className="accountmanagement-main">
        {/* Suspend Account Section */}
        <div className="accountmanagement-card">
          <div className="accountmanagement-card-content">
            {/* Warning Icon */}
            <div className="accountmanagement-icon-container">
              <img
                src={WarningSvg || "/placeholder.svg"}
                alt={t("accountManagement.icons.warning")}
                style={{ width: "24px", height: "24px" }}
              />
            </div>

            <div className="accountmanagement-card-info">
              <h3 className="accountmanagement-card-title">
                {t("accountManagement.cards.suspend.title")}
              </h3>
              <p className="accountmanagement-card-description">
                {t("accountManagement.cards.suspend.description")}
              </p>

              <button
                onClick={handleSuspendAccount}
                disabled={isAccountSuspended}
                className="accountmanagement-suspend-btn"
              >
                {isAccountSuspended
                  ? t("accountManagement.cards.suspend.buttonDisabled")
                  : t("accountManagement.cards.suspend.button")}
              </button>

              <div className="accountmanagement-checkmark-container">
                <img
                  src={CheckmarkSvg || "/placeholder.svg"}
                  alt={t("accountManagement.icons.checkmark")}
                  className="accountmanagement-checkmark-icon"
                />
                <span className="accountmanagement-checkmark-text">
                  {t("accountManagement.cards.suspend.note")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="accountmanagement-card">
          <div className="accountmanagement-card-content">
            {/* Delete Icon */}
            <div className="accountmanagement-icon-container delete">
              <img
                src={DeleteSvg || "/placeholder.svg"}
                alt={t("accountManagement.icons.delete")}
              />
            </div>

            <div className="accountmanagement-card-info">
              <h3 className="accountmanagement-card-title">
                {t("accountManagement.cards.delete.title")}
              </h3>
              <p className="accountmanagement-card-description">
                {t("accountManagement.cards.delete.description")}
              </p>

              <button
                onClick={handleDeleteAccount}
                className="accountmanagement-delete-btn"
              >
                {t("accountManagement.cards.delete.button")}
              </button>

              <div className="accountmanagement-checkmark-container">
                <img
                  src={CheckmarkSvg || "/placeholder.svg"}
                  alt={t("accountManagement.icons.checkmark")}
                  className="accountmanagement-checkmark-icon"
                />
                <span className="accountmanagement-checkmark-text">
                  {t("accountManagement.cards.delete.note")}
                </span>
              </div>
            </div>
          </div>
        </div>


      </main>


      {/* Modals */}
      <SuspendAccountModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={handleConfirmSuspension}
        hasPassword={user?.has_password !== false && !(user?.has_social_login || getStoredUser().has_social_login)} // Hide password step if ANY indication of social login
      />

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirmDelete={handleConfirmDeletion}
        onSuspendInstead={handleSuspendInsteadOfDelete}
        hasPassword={user?.has_password !== false && !(user?.has_social_login || getStoredUser().has_social_login)}
      />

      <AccountDeletionInitiatedModal
        isOpen={showDeletionInitiatedModal}
        onClose={() => setShowDeletionInitiatedModal(false)}
      />

      <SuspensionSuccessModal
        isOpen={showSuspensionSuccessModal}
        onClose={() => setShowSuspensionSuccessModal(false)}
        onRedirect={handleSuspensionSuccessRedirect}
      />
    </div>
  )
}

// Wrapper component for backward compatibility
const AccountManagement = () => {
  const navigate = useNavigate()
  const { userType } = useParams()

  return (
    <div className="accountmanagement-page">
      <Navbar userType={userType} />
      <AccountManagementContent />
    </div>
  )
}

export default AccountManagement
export { AccountManagementContent }
